<template>
  <div>
    <base-card>
      <div class="tw-divide-y tw-divide-gray-200">
        <h3 class="tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900">Email Preferences</h3>
        <div class="tw-flex tw-justify-between tw-items-center tw-pt-3 tw-mt-3" v-if="areEmailPreferencesLoading">
          <div>
            <base-skeleton-loader class="tw-h-3 tw-w-24 tw-mt-1.5" />
            <base-skeleton-loader class="tw-h-2.5 tw-w-36 tw-mt-2.5 tw-mb-1.5" />
          </div>
          <base-skeleton-loader class="tw-h-6 tw-w-10 tw-rounded-full" />
        </div>
        <div class="tw-flex tw-justify-between tw-items-center tw-pt-3 tw-mt-3" v-else>
          <div>
            <div class="tw-font-normal">Recurring Charge Notification</div>
            <div class="tw-text-sm tw-text-gray-600 tw-pr-4">Do you want to be notified every time your plans charge your credit card and refill with credits?</div>
          </div>
          <base-toggle
            :value="recurringChargeNotification"
            @input="updateCommunicationSetting('recurringChargeNotification', !recurringChargeNotification)"
            :loading="preferenceLoadingKey === 'recurringChargeNotification'"
          />
        </div>
      </div>
    </base-card>
    <base-card class="tw-mt-6">
      <div class="tw-divide-y tw-divide-gray-200">
        <div class="tw-space-y-1">
          <h3 class="tw-text-lg tw-leading-6 tw-font-normal tw-text-gray-900 tw-mb-0">Reminders and Promotions</h3>
          <p class="tw-max-w-2xl tw-text-sm tw-text-gray-500">Control which businesses can contact you with marketing emails</p>
        </div>
        <div class="tw-mt-6">
          <dl class="tw-divide-y tw-divide-gray-200 tw-mb-0" v-if="areSubscriptionsLoading">
            <div class="tw-flex tw-justify-between tw-items-center tw-py-2" v-for="i in 3" :key="i">
              <dt class="tw-font-normal"><base-skeleton-loader class="tw-h-3 tw-w-24 tw-my-2" /></dt>
              <dd>
                <base-skeleton-loader class="tw-h-6 tw-w-10 tw-rounded-full" />
              </dd>
            </div>
          </dl>
          <dl class="tw-divide-y tw-divide-gray-200 tw-mb-0" v-else>
            <div class="tw-flex tw-justify-between tw-items-center tw-py-2.5" v-for="(subscription, index) in orderedSubscriptions" :key="index">
              <dt class="tw-font-normal">{{ subscription.businessName }}</dt>
              <dd class="tw-items-center tw-flex">
                <base-toggle
                  :value="subscription.isSubscribed"
                  @input="updateSubscription(subscription.businessId, !subscription.isSubscribed)"
                  :loading="loadingBusinessId === subscription.businessId"
                />
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </base-card>
  </div>
</template>

<script>
import { orderBy } from "lodash";

export default {
  props: {
    userId: {
      type: String,
      isRequired: true,
    },
  },
  data() {
    return {
      areEmailPreferencesLoading: false,
      recurringChargeNotification: false,
      preferenceLoadingKey: null,
      areSubscriptionsLoading: false,
      businessSubscriptions: [],
      loadingBusinessId: null,
    };
  },
  computed: {
    orderedSubscriptions() {
      return orderBy(this.businessSubscriptions, ["businessName"], ["asc"]);
    },
  },
  created() {
    this.getCommunicationSettings();
    this.getSubscribedBusinesses();
  },
  methods: {
    getCommunicationSettings() {
      this.areEmailPreferencesLoading = true;

      this.$axios
        .get(`/users/${this.userId}/communicationsettings`)
        .then((res) => {
          const s = res.data.find((setting) => setting.status && setting.setting === "recurringChargeNotification");
          this.recurringChargeNotification = s ? s.status : false;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error retrieving your communication settings",
            message: "We had an issue retrieving your settings. Our technicians have been notified. You may want to try again.",
            error: { message: err.response.data },
          });
        })
        .finally(() => {
          this.areEmailPreferencesLoading = false;
        });
    },
    updateCommunicationSetting(key, value) {
      this.preferenceLoadingKey = key;

      this.$axios
        .put(`/users/${this.userId}/communicationsettings/${key}`, { status: value })
        .then(() => {
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Setting Changed", message: "Your communication preference was successfully updated" });
          this[key] = value;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error retrieving your communication settings",
            message: "We had an issue retrieving your settings. Our technicians have been notified. You may want to try again.",
            error: { message: err.response.data },
          });
        })
        .finally(() => {
          this.preferenceLoadingKey = null;
        });
    },
    getSubscribedBusinesses() {
      this.areSubscriptionsLoading = true;

      this.$axios
        .get(`/users/${this.userId}/subscribed-businesses`)
        .then((res) => {
          this.businessSubscriptions = res.data.businessSubscriptions;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error retrieving your communication settings",
            message: "We had an issue retrieving your settings. Our technicians have been notified. You may want to try again.",
            error: { message: err.response.data },
          });
        })
        .finally(() => {
          this.areSubscriptionsLoading = false;
        });
    },
    updateSubscription(businessId, isSubscribed) {
      this.loadingBusinessId = businessId;

      this.$axios
        .post(`/users/${this.userId}/subscribed-businesses`, {
          businessId,
          isSubscribed,
        })
        .then(() => {
          const business = this.businessSubscriptions.find((bs) => bs.businessId === businessId);
          business.isSubscribed = isSubscribed;
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Subscription Setting Change",
            message: `Your subscription setting for ${business.businessName} was updated successfully`,
          });
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error saving your communication setting",
            message: "We had an issue saving your setting. Our technicians have been notified. You may want to try again.",
            error: { message: err.response.data },
          });
        })
        .finally(() => {
          this.loadingBusinessId = null;
        });
    },
  },
};
</script>
